import { Track as LKTrack } from 'livekit-client'

import type { Room } from 'livekit-client'
import type { ConnectionReading, ConnectionUplinkSample } from './types/classroom/interfaces'

import { CONNECTION_UPLINK_LOSS_PCT_MIN } from '../config/connection-quality'

interface ConnectionUplinkLiveKitReturn {
  sample: (room: Room) => Promise<ConnectionUplinkSample>
}

interface RemoteInboundRtpStats {
  fractionLost?: number
  roundTripTime?: number
  jitter?: number
}

const PERCENT = 100
const MS_PER_SECOND = 1000
const CPU_LIMITATION_REASON = 'cpu'
const BANDWIDTH_LIMITATION_REASON = 'bandwidth'

const noReading = (): ConnectionReading => ({ lossPct: null, rttMs: null, jitterMs: null })

export const cleanUplink = (): ConnectionUplinkSample => ({
  reading: noReading(),
  limitationReason: null,
  isCpuLimited: false,
  isImpaired: false,
})

const scaled = (value: number | undefined, factor: number): number | null => (value === undefined ? null : value * factor)

const readReading = async (room: Room): Promise<ConnectionReading> => {
  const sender = room.localParticipant.getTrackPublication(LKTrack.Source.Microphone)?.audioTrack?.sender
  if (!sender) return noReading()

  const report = await sender.getStats()
  const outbound = [...report.values()].find((stats) => stats.type === 'outbound-rtp')
  const remoteInbound: RemoteInboundRtpStats | undefined = outbound ? report.get(outbound.remoteId) : undefined

  return {
    lossPct: scaled(remoteInbound?.fractionLost, PERCENT),
    rttMs: scaled(remoteInbound?.roundTripTime, MS_PER_SECOND),
    jitterMs: scaled(remoteInbound?.jitter, MS_PER_SECOND),
  }
}

const readLimitationReasons = async (room: Room): Promise<string[]> => {
  const videoTrack = room.localParticipant.getTrackPublication(LKTrack.Source.Camera)?.videoTrack
  if (!videoTrack) return []

  const layers = await videoTrack.getSenderStats()

  return layers
    .map((layer) => layer.qualityLimitationReason)
    .filter((reason): reason is string => !!reason)
}

export const useConnectionUplinkLiveKit = (): ConnectionUplinkLiveKitReturn => {
  const sample = async (room: Room): Promise<ConnectionUplinkSample> => {
    const [reading, limitationReasons] = await Promise.all([
      readReading(room).catch(() => noReading()),
      readLimitationReasons(room).catch((): string[] => []),
    ])
    const isLossy = (reading.lossPct ?? 0) >= CONNECTION_UPLINK_LOSS_PCT_MIN
    const isBandwidthLimited = limitationReasons.includes(BANDWIDTH_LIMITATION_REASON)

    return {
      reading,
      limitationReason: limitationReasons[0] ?? null,
      isCpuLimited: limitationReasons.includes(CPU_LIMITATION_REASON),
      isImpaired: isLossy || isBandwidthLimited,
    }
  }

  return { sample }
}
